import { useState, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { foldSearch } from '@eis/core/reporting';

/** A catalog view as the service lists it (`dataservice.v1.CatalogDataset`). */
export interface CatalogTreeDataset {
  readonly name: string;
  readonly group: string;
  readonly product?: string;
  readonly datasetKind?: string;
  readonly productVersion?: number;
  readonly fieldCount?: number;
}

/** A custom dataset of "Meine Datensätze". */
export interface CatalogTreeCustom {
  readonly code: string;
  readonly name: string;
  readonly publishedVersion: number;
  readonly hasDraft: boolean;
}

export type CatalogSelection =
  | { readonly kind: 'catalog'; readonly name: string }
  | { readonly kind: 'custom'; readonly code: string };

export interface CatalogTreeProps {
  readonly datasets: readonly CatalogTreeDataset[];
  readonly custom: readonly CatalogTreeCustom[];
  readonly selected?: CatalogSelection;
  readonly onSelect: (selection: CatalogSelection) => void;
  /** The search, owned by the page so it can live in the URL. */
  readonly search: string;
  readonly onSearch: (search: string) => void;
}

const GROUP_ORDER = ['STANDARD', 'PRODUCT', 'WIDE', 'TENANT_CUSTOM'] as const;

/**
 * The tenant's catalog, grouped (SDD-024 W-4, L-2; mockup board 7): Standard, Produkte (one node
 * per product, its `g_` version views collapsed under "Versionen"), Alle Produkte (breit), Eigene
 * Views des Mandanten and Meine Datensätze. Views only — raw tables and `_mat` twins never reach it.
 * One snapshot, searched client-side: the product group is searchable because test tenants carry
 * hundreds of products.
 */
export function CatalogTree({
  datasets,
  custom,
  selected,
  onSelect,
  search,
  onSearch,
}: CatalogTreeProps): ReactNode {
  const { t } = useTranslation('patterns');
  const [closed, setClosed] = useState<ReadonlySet<string>>(new Set(['WIDE', 'TENANT_CUSTOM']));
  // A product's `g_` version views start collapsed under it (W-4 R2, board 7 pin 3).
  const [openVersions, setOpenVersions] = useState<ReadonlySet<string>>(new Set());
  const terms = foldSearch(search.trim());
  const matches = (text: string): boolean => {
    const folded = foldSearch(text).join(' ');
    return terms.every((term) => folded.includes(term));
  };
  const visible = datasets.filter(
    (d) => terms.length === 0 || matches(`${d.name} ${d.product ?? ''}`),
  );
  const visibleCustom = custom.filter((c) => terms.length === 0 || matches(`${c.name} ${c.code}`));

  const toggle = (key: string): void => {
    setClosed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const isSelected = (s: CatalogSelection): boolean =>
    selected?.kind === s.kind &&
    (s.kind === 'catalog'
      ? s.name === (selected as { name: string }).name
      : s.code === (selected as { code: string }).code);

  const groupHeader = (key: string, label: string, count: number): ReactNode => (
    <Button
      size="small"
      color="inherit"
      fullWidth
      aria-expanded={terms.length > 0 || !closed.has(key)}
      onClick={() => {
        toggle(key);
      }}
      endIcon={
        terms.length > 0 || !closed.has(key) ? (
          <ExpandLessIcon fontSize="small" />
        ) : (
          <ExpandMoreIcon fontSize="small" />
        )
      }
      sx={{ justifyContent: 'space-between', fontWeight: 600, textTransform: 'none' }}
    >
      {label} ({count})
    </Button>
  );

  const item = (
    selection: CatalogSelection,
    primary: string,
    secondary?: string,
    depth = 0,
  ): ReactNode => (
    <ListItem
      key={selection.kind === 'catalog' ? `c:${selection.name}` : `d:${selection.code}`}
      disablePadding
    >
      <ListItemButton
        selected={isSelected(selection)}
        onClick={() => {
          onSelect(selection);
        }}
        sx={{ pl: 2 + depth * 2, py: 0.25 }}
        data-dataset={selection.kind === 'catalog' ? selection.name : primary}
      >
        <ListItemText
          primary={primary}
          secondary={secondary}
          slotProps={{ secondary: { variant: 'caption' } }}
        />
      </ListItemButton>
    </ListItem>
  );

  const products = new Map<string, CatalogTreeDataset[]>();
  for (const d of visible.filter((x) => x.group === 'PRODUCT'))
    products.set(d.product ?? '', [...(products.get(d.product ?? '') ?? []), d]);

  return (
    <Stack spacing={1}>
      <TextField
        size="small"
        value={search}
        placeholder={t('reporting.catalog.search')}
        slotProps={{ htmlInput: { 'aria-label': t('reporting.catalog.search') } }}
        onChange={(event) => {
          onSearch(event.target.value);
        }}
      />
      {visible.length === 0 && visibleCustom.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {t('reporting.catalog.empty')}
        </Typography>
      )}
      {GROUP_ORDER.map((group) => {
        const members = visible.filter((d) => d.group === group);
        const label = t(`reporting.catalog.groups.${group}`);
        const open = terms.length > 0 || !closed.has(group);
        if (group === 'PRODUCT') {
          return (
            <Box key={group} component="section" aria-label={label} data-group={label}>
              {groupHeader(group, label, products.size)}
              <Collapse in={open}>
                <Box>
                  {[...products.entries()].map(([product, views]) => {
                    const unions = views.filter((v) => (v.productVersion ?? 0) === 0);
                    const versions = views.filter((v) => (v.productVersion ?? 0) > 0);
                    return (
                      <Box key={product}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', pl: 2, pt: 0.5, fontWeight: 600 }}
                        >
                          {product}
                        </Typography>
                        <List dense disablePadding aria-label={product}>
                          {unions.map((v) =>
                            item({ kind: 'catalog', name: v.name }, v.name, undefined, 1),
                          )}
                        </List>
                        {versions.length > 0 && (
                          <>
                            <Button
                              size="small"
                              color="inherit"
                              aria-expanded={openVersions.has(product)}
                              onClick={() => {
                                setOpenVersions((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(product)) next.delete(product);
                                  else next.add(product);
                                  return next;
                                });
                              }}
                              sx={{ pl: 4, textTransform: 'none' }}
                            >
                              {t('reporting.catalog.versions', { count: versions.length })}
                            </Button>
                            <Collapse in={openVersions.has(product)}>
                              <List
                                dense
                                disablePadding
                                aria-label={t('reporting.catalog.versions', {
                                  count: versions.length,
                                })}
                              >
                                {versions.map((v) =>
                                  item(
                                    { kind: 'catalog', name: v.name },
                                    v.name,
                                    t('reporting.catalog.version', { version: v.productVersion }),
                                    2,
                                  ),
                                )}
                              </List>
                            </Collapse>
                          </>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Collapse>
            </Box>
          );
        }
        return (
          <Box key={group} component="section" aria-label={label} data-group={label}>
            {groupHeader(group, label, members.length)}
            <Collapse in={open}>
              <List dense disablePadding aria-label={label}>
                {members.map((d) =>
                  item(
                    { kind: 'catalog', name: d.name },
                    d.name,
                    d.fieldCount === undefined
                      ? undefined
                      : t('reporting.catalog.fields', { count: d.fieldCount }),
                  ),
                )}
              </List>
            </Collapse>
          </Box>
        );
      })}
      <Box
        component="section"
        aria-label={t('reporting.catalog.groups.CUSTOM')}
        data-group={t('reporting.catalog.groups.CUSTOM')}
      >
        {groupHeader('CUSTOM', t('reporting.catalog.groups.CUSTOM'), visibleCustom.length)}
        <Collapse in={terms.length > 0 || !closed.has('CUSTOM')}>
          <List dense disablePadding aria-label={t('reporting.catalog.groups.CUSTOM')}>
            {visibleCustom.map((c) =>
              item(
                { kind: 'custom', code: c.code },
                c.name,
                [
                  c.code,
                  c.publishedVersion > 0
                    ? t('reporting.catalog.published', { version: c.publishedVersion })
                    : '',
                  c.hasDraft ? t('reporting.catalog.draft') : '',
                ]
                  .filter((x) => x !== '')
                  .join(' · '),
              ),
            )}
          </List>
        </Collapse>
      </Box>
    </Stack>
  );
}
